import moment from 'moment';
import log from '$$log';
import globalToast from '$$vueCmp/toast';
import constants from '$$helpers/constants';

class AutoSchedulePatches {
  constructor() {
    this.successorsConstraintDate = {};
    this.predecessorsConstraintDate = {};
    this.projectDates = {};
    this.ganttInstance = null;
    this.updateTasksCounter = 0;
    this.lastUpdatedTasks = [];
  }

  init(instance) {
    this.ganttInstance = instance;
  }

  _clearConstraintDates() {
    this.successorsConstraintDate = {};
    this.predecessorsConstraintDate = {};
  }

  _isMilestone(task) {
    return task.type === this.ganttInstance.config.types.milestone;
  }

  _isButton(taskID) {
    return this.ganttInstance.getTask(taskID).type === this.ganttInstance.config.types.button;
  }

  _filterTasks(tasks) {
    return tasks.filter(i => !(this._isButton(i.target) || this._isButton(i.source)));
  }

  _hasSameParent(link) {
    return link.targetParent === link.sourceParent;
  }

  _hasDirectLink(task, id) {
    return task.$target.length > 0 && task.$target.includes(id);
  }

  _hasChanges() {
    return Object.keys(this.successorsConstraintDate).length > 0 || Object.keys(this.predecessorsConstraintDate).length > 0;
  }

  _compareArraysByField(firstArray, secondArray, field) {
    if (firstArray.length === 0 || firstArray.length !== secondArray.length) {
      return false;
    }

    return firstArray.filter((item, index) => item[field] !== secondArray[index][field]).length === 0;
  }

  _calculateConstraintDate(date, lag) {
    const instance = this.ganttInstance;

    if (Math.sign(lag) > 0) {
      const firstDate = instance.calculateToDuration(date, lag + 1, instance.config.duration_unit).endDate;

      return instance.calculateToDuration(firstDate, lag - 1, instance.config.duration_unit).endDate;
    }

    return instance.calculateToDuration(date, lag, instance.config.duration_unit).endDate;
  }

  checkTimelineDates(task) {
    const instance = this.ganttInstance;
    const spaceBefore = ['hour', 'day'].includes(instance.config.durationData.mode) ? -2 : -1;
    const newStartDate = instance.calculateEndDate(task.start_date, spaceBefore, instance.config.durationData.mode);
    const totalStartDate = instance.calculateEndDate(instance.getTaskByIndex(0).start_date, spaceBefore, instance.config.durationData.mode);
    const isNearEndDate = new Date(task.end_date).valueOf() >= new Date(instance.config.end_date).valueOf();
    const isNearStartDate = newStartDate.valueOf() <= new Date(instance.config.start_date).valueOf();
    const isFarFromTotalStartDate = new Date(instance.config.start_date).valueOf() < totalStartDate.valueOf();

    if (isNearEndDate || isNearStartDate || isFarFromTotalStartDate) {
      const t = log.time('ganttRender.instance');
      instance.render();
      t.end();

      return true;
    }

    return false;
  }

  _updatePredecessors(predecessor) {
    const instance = this.ganttInstance;
    const target = instance.getTask(predecessor.target);
    const source = instance.getTask(predecessor.source);
    const link = instance.getLink(predecessor.id);
    const originalSource = instance.getTask(link.source);
    const additionalLag = predecessor.additionalLag || 0;

    if (this._isMilestone(target) || this._hasSameParent(predecessor)) {
      target.constraint_type = instance.config.constraint_types.SNLT;

      if (!this._hasDirectLink(target, source.id)) {
        predecessor.lag += instance.calculateDuration(source.end_date, originalSource.end_date, source);
      }

      if (!this.predecessorsConstraintDate[predecessor.target]) {
        target.constraint_date = instance.calculateToDuration(source.end_date, predecessor.lag - additionalLag, instance.config.duration_unit).endDate;
      } else {
        const tmpDate = instance.calculateToDuration(source.end_date, predecessor.lag - additionalLag, instance.config.duration_unit).endDate;

        if (moment(this.predecessorsConstraintDate[predecessor.target]).isAfter(tmpDate)) {
          target.constraint_date = tmpDate;
        }
      }

      target.constraint_date = instance.getClosestWorkTime({ date: target.constraint_date, dir: 'future', task: target });
      this.predecessorsConstraintDate[predecessor.target] = target.constraint_date;
    } else {
      target.constraint_type = instance.config.constraint_types.SNLT;
      if (!this._hasDirectLink(target, source.id)) {
        predecessor.lag += instance.calculateDuration(source.end_date, originalSource.end_date, source);
      }
      if (!this.predecessorsConstraintDate[predecessor.target]) {
        target.constraint_date = instance.calculateToDuration(source.end_date, predecessor.lag - additionalLag, instance.config.duration_unit).endDate;
      } else {
        const tmpDate = instance.calculateToDuration(source.end_date, predecessor.lag - additionalLag, instance.config.duration_unit).endDate;

        if (moment(this.predecessorsConstraintDate[predecessor.target]).isAfter(tmpDate)) {
          target.constraint_date = tmpDate;
        }
      }

      target.constraint_date = instance.getClosestWorkTime({ date: target.constraint_date, dir: 'future', task: target });
      this.predecessorsConstraintDate[predecessor.target] = target.constraint_date;
    }
  }

  _updateSuccessors(successor) {
    const instance = this.ganttInstance;
    const target = instance.getTask(successor.target);
    const source = instance.getTask(successor.source);
    const targetParent = instance.getTask(successor.targetParent);
    const link = instance.getLink(successor.id);
    const originalSource = instance.getTask(link.source);
    const additionalLag = successor.additionalLag || 0;

    if (this._isMilestone(target) || this._hasSameParent(successor)) {
      target.constraint_type = instance.config.constraint_types.SNLT;

      if (!this._hasDirectLink(target, source.id)) {
        successor.lag += instance.calculateDuration(source.end_date, originalSource.end_date, source);
      }

      if (!this.successorsConstraintDate[successor.target]) {
        target.constraint_date = instance.calculateToDuration(source.end_date, successor.lag - additionalLag, instance.config.duration_unit).endDate;
      } else {
        const tmp = instance.calculateToDuration(source.end_date, successor.lag - additionalLag, instance.config.duration_unit).endDate;

        if (moment(this.successorsConstraintDate[successor.target]).isBefore(tmp)) {
          target.constraint_date = tmp;
        }
      }

      target.constraint_date = instance.getClosestWorkTime({ date: target.constraint_date, dir: 'future', task: target });
      this.successorsConstraintDate[successor.target] = target.constraint_date;
    } else {
      target.constraint_type = instance.config.constraint_types.SNLT;
      if (!this._hasDirectLink(target, source.id)) {
        successor.lag += instance.calculateDuration(source.end_date, originalSource.end_date, source);
      }
      if (!this.successorsConstraintDate[successor.target]) {
        target.constraint_date = instance.calculateToDuration(source.end_date, successor.lag - additionalLag, instance.config.duration_unit).endDate;
      } else {
        const tmp = instance.calculateToDuration(source.end_date, successor.lag - additionalLag, instance.config.duration_unit).endDate;

        if (moment(this.successorsConstraintDate[successor.target]).isBefore(tmp)) {
          target.constraint_date = tmp;
        }
      }

      target.constraint_date = instance.getClosestWorkTime({ date: target.constraint_date, dir: 'future', task: target });

      // if (!this._hasSameParent(successor)) {
      //   const test = instance.getTask(66131008);

      //   test.constraint_type = instance.config.constraint_types.FNET;
      //   test.constraint_date = targetParent.end_date;
      //   // targetParent.end_date = instance.calculateEndDate({
      //   //   start_date: target.constraint_date,
      //   //   duration: targetParent.duration,
      //   //   task: targetParent,
      //   //   unit: instance.config.duration_unit,
      //   // });
      // }

      this.successorsConstraintDate[successor.target] = target.constraint_date;
    }
  }

  onBeforeAutoSchedule(task) {
    // if (this.ganttInstance._preventCustomAutoscheduling) {
    // return true;
    // }

    this._clearConstraintDates();
    const instance = this.ganttInstance;

    const targetTask = task.$source.length && instance.getTask(instance.getLink(task.$source[task.$source.length - 1]).target);
    const hasLinkToProject = task.$source.length && targetTask.type === constants.TASK_TYPES.project;

    if (task.$target.length && task.type !== constants.TASK_TYPES.project && !hasLinkToProject) {
      task.constraint_type = instance.config.constraint_types.ASAP;
      task.constraint_date = null;
    } else {
      const parent = task.parent ? instance.getTask(task.parent) : null;
      const predecessors = this._filterTasks(instance._getPredecessors(task, false));
      const successors = this._filterTasks(instance._getSuccessors(task, false));

      if (parent) {
        predecessors.forEach(predecessor => this._updatePredecessors(predecessor));
      }

      successors.forEach(successor => this._updateSuccessors(successor));

      if (!this._hasChanges()) {
        return false;
      }

      // console.log('predecessorsConstraintDate', _.clone(innerSettings.predecessorsConstraintDate));
      // console.log('successorsConstraintDate', _.clone(innerSettings.successorsConstraintDate));

      this._clearConstraintDates();
    }

    return true;
  }

  checkLinks(id, task) {
    const instance = this.ganttInstance;
    const parent = instance.getTask(task.parent);

    if (instance.isSummaryTask(parent) && instance.calculateTaskLevel(task.parent) <= 1) {
      const children = instance.getChildren(parent.id).filter(taskID => !this._isButton(taskID));

      if (children.length === 0) {
        parent.$target.forEach(targetId => {
          instance.isLinkExists(targetId) && instance.deleteLink(targetId);
        });
        parent.$source.forEach(sourceId => {
          instance.isLinkExists(sourceId) && instance.deleteLink(sourceId);
        });
      }
    }
  }

  preventLinkToEmptyProject(id, link) {
    const instance = this.ganttInstance;
    const target = instance.getTask(link.target);

    if (instance.isSummaryTask(target)) {
      const children = instance.getChildren(link.target).filter(taskID => !this._isButton(taskID));

      if (children.length === 0) {
        return false;
      }
    }

    return true;
  }

  preventLinkFromEmptyProject(id, link) {
    const instance = this.ganttInstance;
    const source = instance.getTask(link.source);

    if (instance.isSummaryTask(source)) {
      const children = instance.getChildren(link.source).filter(taskID => !this._isButton(taskID));

      if (children.length === 0) {
        return false;
      }
    }

    return true;
  }

  preventLinkByLimits(id, link) {
    const instance = this.ganttInstance;
    const source = instance.getTask(link.source);
    const sourceTargets = source.$target.map(linkId => +instance.getLink(linkId).source);
    const isTargetToExistSource = sourceTargets.includes(+link.target);
    const sourceSources = source.$source.map(linkId => +instance.getLink(linkId).target);
    const isTargetRepeat = sourceSources.includes(+link.target);

    const needPrevent = instance.isCircularLink(link) || isTargetToExistSource || isTargetRepeat;

    if (needPrevent) {
      globalToast.warning(__('gantt_msg_dependency_limit_error'));
    }

    return !needPrevent;
  }

  saveDatesOfProjectTasks() {
    this.projectDates = {};
    this.ganttInstance.getTaskBy(task => task.type === 'project').forEach(task => {
      this.projectDates[task.id] = { start_date: new Date(task.start_date), end_date: new Date(task.end_date) };
    });
  }

  prepareParentsForUpdate() {
    const instance = this.ganttInstance;
    const updatedTasksIDs = [];

    instance.getTaskBy(task => task.type === 'project').forEach(task => {
      if (this.projectDates[task.id]) {
        const oldValue = this.projectDates[task.id];

        if (oldValue.start_date.valueOf() !== task.start_date.valueOf() || oldValue.end_date.valueOf() !== task.end_date.valueOf()) {
          updatedTasksIDs.push(task.id);
        }
      }
    });

    if (updatedTasksIDs.length) {
      const updatedTasks = updatedTasksIDs.map(taskId => instance.getTask(taskId));

      updatedTasks.some(task => this.checkTimelineDates(task));

      const tasksToUpdate = updatedTasks.filter(task => task.type !== constants.TASK_TYPES.button && task.id !== 1);
      const isEqualWithLastUpdated = this._compareArraysByField(tasksToUpdate, this.lastUpdatedTasks, 'id');

      if (isEqualWithLastUpdated) {
        this.updateTasksCounter++;

        if (this.updateTasksCounter > 5) {
          this.autoScheduleCircularLink([], this.lastUpdatedTasks);
          this.lastUpdatedTasks = [];

          return;
        }
      } else {
        this.updateTasksCounter = 0;
      }

      if (tasksToUpdate.length) {

        // TODO
        // instance.callEvent('afterTasksUpdated', [masterType, tasksToUpdate]);
        // instance.callEvent('tasks:correctDates', [true]);
      }

      return tasksToUpdate;
    }

    return [];
  }

  async sendTasksUpdate(id, updatedTasksIDs) {
    const instance = this.ganttInstance;

    instance.getTaskBy(task => task.type === 'project').forEach(task => {
      if (this.projectDates[task.id]) {
        const oldValue = this.projectDates[task.id];

        if (oldValue.start_date.valueOf() !== task.start_date.valueOf() || oldValue.end_date.valueOf() !== task.end_date.valueOf()) {
          updatedTasksIDs.push(task.id);
        }
      }
    });

    if (updatedTasksIDs.length) {
      const updatedTasks = updatedTasksIDs.map(taskId => instance.getTask(taskId));

      updatedTasks.some(task => this.checkTimelineDates(task));

      await this._updateTasks(updatedTasks);
    }
  }

  async _updateTasks(tasksArray, masterType, actionHash, taskName) {
    const instance = this.ganttInstance;
    const tasksToUpdate = tasksArray.filter(task => task.type !== constants.TASK_TYPES.button && task.id !== 1);
    const isEqualWithLastUpdated = this._compareArraysByField(tasksToUpdate, this.lastUpdatedTasks, 'id');

    if (isEqualWithLastUpdated) {
      this.updateTasksCounter++;

      if (this.updateTasksCounter > 5) {
        this.autoScheduleCircularLink([], this.lastUpdatedTasks);
        this.lastUpdatedTasks = [];

        return Promise.resolve();
      }
    } else {
      this.updateTasksCounter = 0;
    }

    if (tasksToUpdate.length) {
      // TODO
      // instance.callEvent('afterTasksUpdated', [masterType, tasksToUpdate]);
      // instance.callEvent('tasks:correctDates', [true]);
    }

    return Promise.resolve();
  }

  async autoScheduleCircularLink(cycles, tasks) {
    const instance = this.ganttInstance;
    const ganttData = instance.serialize();
    const linksToRemove = cycles.map(cycle => Math.max(...cycle.links));

    linksToRemove.forEach(linkID => instance.deleteLink(linkID));

    // TODO replace to dispatch
    await webix.ajax().headers({ 'Content-type': 'application/json' })
      .post('/api/tasks/onAutoScheduleCircularLink', {
        gantt_id: instance.ganttDataApi.getActiveProjectId(),
        cycles,
        tasks: tasks.map(task => ({ text: task.text, id: task.id })),
        linksToRemove,
        ganttData,
      });

    if (linksToRemove.length > 0) {
      instance.autoSchedule();
    }
  }
}

export default new AutoSchedulePatches();
